/**
 * The table every list screen uses.
 *
 * Sortable by declaring `sortValue` on a column; sorting is internal by default
 * and controlled when the server does it. Loading is skeleton rows in the real
 * column layout, not a spinner over a blank box — the shape of the answer
 * should be visible before the answer is.
 *
 * "Virtualization-friendly" means it does not own scrolling or measure
 * anything: pass `maxHeight` plus `scrollRef` and the spacer heights from
 * whatever windowing library you like, and the table renders padding rows above
 * and below the slice you hand it. Nothing here has to change to enable that.
 */
import { useMemo, useState, type ReactNode, type Ref } from 'react'
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

export type SortDirection = 'asc' | 'desc'

export interface SortState {
  columnId: string
  direction: SortDirection
}

export interface DataTableColumn<TRow> {
  id: string
  header: ReactNode
  /** Cell contents. Keep money inside a `<Money>` so columns align. */
  cell: (row: TRow) => ReactNode
  /**
   * Makes the column sortable. Return a comparable primitive; nulls always sort
   * last regardless of direction, because "unknown" is not "smallest".
   */
  sortValue?: (row: TRow) => string | number | null | undefined
  /** Numbers go right. Everything else goes left. */
  align?: 'left' | 'right' | 'center'
  /** A CSS width — with any width set, the table switches to fixed layout. */
  width?: string
  headerClassName?: string
  cellClassName?: string
  /** Hide below `sm`. Use for columns that are context rather than the point. */
  hideBelowSm?: boolean
  /** Hide below `lg`. */
  hideBelowLg?: boolean
}

export interface DataTableProps<TRow> {
  columns: DataTableColumn<TRow>[]
  rows: TRow[]
  rowKey: (row: TRow) => string
  /** Sort applied on first render when the table sorts itself. */
  defaultSort?: SortState
  /** Pass with `onSortChange` to control sorting (e.g. server-side). */
  sort?: SortState | null
  onSortChange?: (sort: SortState | null) => void
  /** True when the server is doing the ordering — the table will not re-sort. */
  manualSort?: boolean
  loading?: boolean
  skeletonRows?: number
  /** Rendered in place of the body when `rows` is empty — an `<EmptyState>`. */
  empty?: ReactNode
  onRowClick?: (row: TRow) => void
  /** Marks a row as selected/current, e.g. the transaction open in a drawer. */
  isRowActive?: (row: TRow) => boolean
  /** Turns the wrapper into a scroll container, e.g. `"60vh"`. */
  maxHeight?: string
  scrollRef?: Ref<HTMLDivElement>
  /** Spacer heights in pixels when a windowing library is feeding `rows`. */
  virtualPadding?: { top: number; bottom: number }
  /** Sticky header. On by default — forty rows scroll past the header fast. */
  stickyHeader?: boolean
  caption?: string
  className?: string
}

export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  defaultSort,
  sort: controlledSort,
  onSortChange,
  manualSort = false,
  loading = false,
  skeletonRows = 8,
  empty,
  onRowClick,
  isRowActive,
  maxHeight,
  scrollRef,
  virtualPadding,
  stickyHeader = true,
  caption,
  className,
}: DataTableProps<TRow>) {
  const [internalSort, setInternalSort] = useState<SortState | null>(defaultSort ?? null)
  const isControlled = controlledSort !== undefined
  const sort = isControlled ? controlledSort : internalSort

  const sorted = useMemo(() => {
    if (manualSort || !sort) return rows
    const column = columns.find((candidate) => candidate.id === sort.columnId)
    if (!column?.sortValue) return rows

    const factor = sort.direction === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const left = column.sortValue!(a)
      const right = column.sortValue!(b)
      // Missing values sink to the bottom either way round.
      if (left === null || left === undefined) return right === null || right === undefined ? 0 : 1
      if (right === null || right === undefined) return -1
      if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor
      return String(left).localeCompare(String(right), undefined, { numeric: true }) * factor
    })
  }, [rows, sort, columns, manualSort])

  const toggle = (columnId: string) => {
    const next: SortState | null =
      sort?.columnId === columnId
        ? sort.direction === 'asc'
          ? { columnId, direction: 'desc' }
          : null
        : { columnId, direction: 'asc' }

    if (!isControlled) setInternalSort(next)
    onSortChange?.(next)
  }

  const fixedLayout = columns.some((column) => column.width)

  return (
    <div
      ref={scrollRef}
      className={cn(
        'border-hairline bg-panel relative rounded-lg border',
        maxHeight ? 'overflow-auto' : 'overflow-x-auto',
        className,
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <Table className={cn(fixedLayout && 'table-fixed')}>
        {caption && <caption className="sr-only">{caption}</caption>}

        <TableHeader
          className={cn(stickyHeader && 'bg-panel sticky top-0 z-10 [&_tr]:border-hairline')}
        >
          <TableRow className="hover:bg-transparent">
            {columns.map((column) => {
              const sortable = Boolean(column.sortValue)
              const active = sort?.columnId === column.id
              const SortIcon = !active
                ? ChevronsUpDownIcon
                : sort.direction === 'asc'
                  ? ArrowUpIcon
                  : ArrowDownIcon

              return (
                <TableHead
                  key={column.id}
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'eyebrow h-9',
                    column.align === 'right' && 'text-right',
                    column.align === 'center' && 'text-center',
                    column.hideBelowSm && 'hidden sm:table-cell',
                    column.hideBelowLg && 'hidden lg:table-cell',
                    column.headerClassName,
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => toggle(column.id)}
                      className={cn(
                        'hover:text-foreground focus-visible:outline-ring inline-flex items-center gap-1 uppercase focus-visible:outline-2 focus-visible:outline-offset-2',
                        active && 'text-foreground',
                        column.align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      {column.header}
                      <SortIcon className="size-3 opacity-60" aria-hidden />
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading &&
            Array.from({ length: skeletonRows }, (_, index) => (
              <TableRow key={`skeleton-${index}`} className="hover:bg-transparent">
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(
                      column.hideBelowSm && 'hidden sm:table-cell',
                      column.hideBelowLg && 'hidden lg:table-cell',
                    )}
                  >
                    <Skeleton
                      className={cn('h-4', column.align === 'right' ? 'ml-auto w-16' : 'w-24')}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!loading && virtualPadding && virtualPadding.top > 0 && (
            <tr aria-hidden style={{ height: virtualPadding.top }} />
          )}

          {!loading &&
            sorted.map((row) => {
              const interactive = Boolean(onRowClick)
              return (
                <TableRow
                  key={rowKey(row)}
                  onClick={interactive ? () => onRowClick?.(row) : undefined}
                  onKeyDown={
                    interactive
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onRowClick?.(row)
                          }
                        }
                      : undefined
                  }
                  tabIndex={interactive ? 0 : undefined}
                  role={interactive ? 'button' : undefined}
                  data-active={isRowActive?.(row) || undefined}
                  className={cn(
                    'border-hairline',
                    // Hover is instant: a transition on a row you are scanning
                    // reads as lag, not polish.
                    interactive && 'hover:bg-panel-2 cursor-pointer transition-none',
                    'data-[active=true]:bg-panel-2',
                    'focus-visible:outline-ring focus-visible:outline-2 focus-visible:-outline-offset-2',
                  )}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(
                        'py-2',
                        column.align === 'right' && 'text-right',
                        column.align === 'center' && 'text-center',
                        column.hideBelowSm && 'hidden sm:table-cell',
                        column.hideBelowLg && 'hidden lg:table-cell',
                        column.cellClassName,
                      )}
                    >
                      {column.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}

          {!loading && virtualPadding && virtualPadding.bottom > 0 && (
            <tr aria-hidden style={{ height: virtualPadding.bottom }} />
          )}

          {!loading && sorted.length === 0 && empty && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="p-3">
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
